import { useState } from "react";

import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import {
  CheckCircle,
  Download,
  Eye,
  Loader2,
  MoreHorizontal,
  Pencil,
  Trash2,
  Undo2,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { SUPPLIER_PAYMENTS_JOURNAL_CODE } from "~/hooks/purchasing/useSupplierPayments";
import type { AccountMovement } from "~/types/contabilidad";

interface MovimientoTableProps {
  movements: AccountMovement[];
  isLoading: boolean;
  onEdit: (movement: AccountMovement) => void;
  onDelete: (id: string) => void;
  onPublish: (id: string) => void;
  onDownload: (movement: AccountMovement) => void | Promise<void>;
  onReturnToSupplierPayments: (id: string) => void;
}

/** A draft asiento created by Pagos a Proveedor lives in the PAGO_PROVEEDORES NACIONALES journal.
 * Removing it reverses the payment (documents go back to pending), so we surface it as
 * "Devolver a Pago Proveedores" instead of a plain "Eliminar". */
const isSupplierPaymentDraft = (movement: AccountMovement) =>
  !movement.is_published &&
  movement.journal?.code === SUPPLIER_PAYMENTS_JOURNAL_CODE;

export function MovimientoTable({
  movements,
  isLoading,
  onEdit,
  onDelete,
  onPublish,
  onDownload,
  onReturnToSupplierPayments,
}: MovimientoTableProps) {
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [movementToDelete, setMovementToDelete] =
    useState<AccountMovement | null>(null);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [movementToReturn, setMovementToReturn] =
    useState<AccountMovement | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownloadClick = async (movement: AccountMovement) => {
    setDownloadingId(movement.id);
    try {
      await onDownload(movement);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDeleteClick = (movement: AccountMovement) => {
    setMovementToDelete(movement);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (movementToDelete) {
      onDelete(movementToDelete.id);
      setDeleteDialogOpen(false);
      setMovementToDelete(null);
    }
  };

  const handleReturnClick = (movement: AccountMovement) => {
    setMovementToReturn(movement);
    setReturnDialogOpen(true);
  };

  const handleConfirmReturn = () => {
    if (movementToReturn) {
      onReturnToSupplierPayments(movementToReturn.id);
      setReturnDialogOpen(false);
      setMovementToReturn(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (movements.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-muted-foreground">
          No hay comprobantes registrados.
        </p>
        <p className="text-sm text-muted-foreground">
          Crea un nuevo comprobante para comenzar.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nº</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead>Referencia</TableHead>
              <TableHead>Diario</TableHead>
              <TableHead>Empresa</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="w-[70px]">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {movements.map((movement, index) => (
              <TableRow
                key={movement.id}
                className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/50"
                style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                onClick={() =>
                  navigate(`/contabilidad/movimientos/${movement.id}`)
                }
              >
                <TableCell className="font-mono text-sm">
                  {movement.move_number}
                </TableCell>
                <TableCell>
                  {dayjs(movement.move_date).format("DD/MM/YYYY")}
                </TableCell>
                <TableCell className="font-medium">{movement.ref}</TableCell>
                <TableCell>
                  {movement.journal?.code} - {movement.journal?.name}
                </TableCell>
                <TableCell>{movement.company?.name ?? "-"}</TableCell>
                <TableCell>
                  <Badge
                    variant={movement.is_published ? "default" : "secondary"}
                    className={
                      movement.is_published
                        ? "bg-green-100 text-green-800"
                        : "bg-yellow-100 text-yellow-800"
                    }
                  >
                    {movement.is_published ? "Publicado" : "Borrador"}
                  </Badge>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Abrir menú</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() =>
                          navigate(`/contabilidad/movimientos/${movement.id}`)
                        }
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Ver Detalle
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={(e) => {
                          e.preventDefault();
                          void handleDownloadClick(movement);
                        }}
                        disabled={downloadingId === movement.id}
                      >
                        {downloadingId === movement.id ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="mr-2 h-4 w-4" />
                        )}
                        {downloadingId === movement.id
                          ? "Descargando..."
                          : "Descargar Excel"}
                      </DropdownMenuItem>
                      {!movement.is_published && (
                        <>
                          {/* A pago-a-proveedor asiento is owned by the AP subledger and must not be
                              hand-edited (the DB trigger blocks it too). Correct it via "Devolver a
                              Pago Proveedores" and re-register the pago. */}
                          {!isSupplierPaymentDraft(movement) && (
                            <>
                              <DropdownMenuItem onClick={() => onEdit(movement)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                            </>
                          )}
                          <DropdownMenuItem
                            onClick={() => onPublish(movement.id)}
                          >
                            <CheckCircle className="mr-2 h-4 w-4" />
                            Publicar
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {isSupplierPaymentDraft(movement) ? (
                            <DropdownMenuItem
                              onClick={() => handleReturnClick(movement)}
                            >
                              <Undo2 className="mr-2 h-4 w-4" />
                              Devolver a Pago Proveedores
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => handleDeleteClick(movement)}
                              className="text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Eliminar
                            </DropdownMenuItem>
                          )}
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar comprobante?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará el comprobante "{movementToDelete?.ref}".
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Devolver borrador a Pago Proveedores?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Se revertirá el pago del comprobante "{movementToReturn?.ref}" y
              sus documentos volverán a quedar pendientes de pago en Pagos a
              Proveedor. El borrador se eliminará de Comprobantes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmReturn}>
              Devolver a Pago Proveedores
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
